import { Input } from '@/marketing/components/ui/input';
import type { VoucherSort } from '@/marketing/models/voucher';
import {
  toVoucherCampaignFilter,
  toVoucherCampaignFilterValue,
  type VoucherFilterState,
  type VoucherStatusFilter,
} from '@/marketing/lib/voucherListParams';
import type { InfluencerCampaign } from '@/marketing/models/influencerCampaign';

const SELECT_CLASS =
  'tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2 tw-text-sm tw-text-foreground';

const SORT_OPTIONS: Array<{ value: VoucherSort; label: string }> = [
  { value: 'createdAt', label: 'Newest' },
  { value: 'code', label: 'Code A-Z' },
  { value: 'orders', label: 'Most orders' },
  { value: 'revenue', label: 'Most revenue' },
  { value: 'commission', label: 'Most commission' },
];

interface VoucherFilterBarProps {
  filters: VoucherFilterState;
  onChange: (filters: Partial<VoucherFilterState>) => void;
  resultLabel: string;
  affiliateCampaigns: Array<Pick<InfluencerCampaign, 'campaignId' | 'name'>>;
}

export function VoucherFilterBar({ filters, onChange, resultLabel, affiliateCampaigns }: VoucherFilterBarProps) {
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <Input
        value={filters.search}
        onChange={(event) => onChange({ search: event.target.value })}
        placeholder="Search code or handle…"
        className="tw-w-[240px]"
      />
      <select
        value={filters.status}
        onChange={(event) => onChange({ status: event.target.value as VoucherStatusFilter })}
        className={SELECT_CLASS}
      >
        <option value="all">Status: All</option>
        <option value="active">Active</option>
        <option value="disabled">Disabled</option>
      </select>
      <select
        aria-label="Campaign"
        value={toVoucherCampaignFilterValue(filters)}
        onChange={(event) => onChange(toVoucherCampaignFilter(event.target.value))}
        className={SELECT_CLASS}
      >
        <option value="all">Campaign: All codes</option>
        <option value="personal">Personal codes</option>
        <option value="campaign">All campaign codes</option>
        {affiliateCampaigns.map((campaign) => (
          <option key={campaign.campaignId} value={campaign.campaignId}>
            {campaign.name}
          </option>
        ))}
        {/* Keeps the select truthful when the chosen campaign has left the affiliate list, e.g. it was deleted. */}
        {filters.campaignId && !affiliateCampaigns.some((campaign) => campaign.campaignId === filters.campaignId) && (
          <option value={filters.campaignId}>Unknown campaign</option>
        )}
      </select>
      <select
        value={filters.sort}
        onChange={(event) => onChange({ sort: event.target.value as VoucherSort })}
        className={SELECT_CLASS}
      >
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            Sort: {option.label}
          </option>
        ))}
      </select>
      <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
        Sales from
        <input
          type="date"
          value={filters.fromDate}
          onChange={(event) => onChange({ fromDate: event.target.value })}
          className={SELECT_CLASS}
        />
        to
        <input
          type="date"
          value={filters.toDate}
          onChange={(event) => onChange({ toDate: event.target.value })}
          className={SELECT_CLASS}
        />
      </label>
      <span className="tw-ml-auto tw-text-xs tw-text-muted-foreground">{resultLabel}</span>
    </div>
  );
}
